import { useState } from 'react'

import { Box, Divider, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { JoinPremiumBundleFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { BundleCheckoutModal } from 'modules/ats/pages/JobPostingMarketplace/BundleCheckoutModal'
import { PremiumOfferAvatar } from 'modules/ats/pages/JobPostingMarketplace/PremiumOfferAvatar'
import { PremiumOfferBookedBadge } from 'modules/ats/pages/JobPostingMarketplace/PremiumOfferBookedBadge'
import 'modules/ats/pages/JobPostingMarketplace/index.styles.css'

interface Props {
  bundle: JoinPremiumBundleFragment
}

export const PremiumBundleCard = ({ bundle }: Props) => {
  const { access } = useCurrentSession()
  const isBooked = bundle.isBooked
  const [purchaseModalOpenId, setPurchaseModalOpenId] = useState<string | null>(null)

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat(access.locale, {
      style: 'currency',
      currency: bundle.pricing.currencyCode,
      minimumFractionDigits: 2,
    }).format(amount / 100)
  }

  const hasDiscount = bundle.pricing.reducedPercentage > 0
  const originalAmount = formatPrice(bundle.pricing.originalAmount)
  const bundlePrice = hasDiscount ? formatPrice(bundle.pricing.reducedAmount) : originalAmount

  return (
    <Box className={'JobPostingMarketplace_premiumOfferCardStyle'}>
      <Box padding="s16" gap="s8">
        <Box flexDirection="row" gap="s4">
          {bundle.products.map((product) => (
            <PremiumOfferAvatar
              key={`premium-bundle-avatar-${product.id}`}
              identifier={product.id}
              tooltipContent={
                <Box gap="s8">
                  <Box gap="s2">
                    <Text.Normal size="200" weight="semibold">
                      {product.jobBoard.name} -{' '}
                      {i18n.t('ats.job_details.marketplace.common.duration_days', {
                        duration: product.durationDays,
                      })}
                    </Text.Normal>
                    <Text.Normal size="200">{product.jobBoard.description}</Text.Normal>
                  </Box>
                  <Box gap="s2">
                    <Text.Normal size="200" weight="semibold">
                      {product.name}
                    </Text.Normal>
                    <Text.Normal size="200">{product.description}</Text.Normal>
                  </Box>
                </Box>
              }
              logoUrl={product.jobBoard.logoUrl}
            />
          ))}
        </Box>
        <Text.Normal color="grey1000" size="300" weight="semibold">
          {bundle.name}
        </Text.Normal>
      </Box>
      <Divider />
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" padding="s16">
        <Box flexDirection="row" gap="s6">
          {hasDiscount ? (
            <>
              <Text.Normal size="200" color="grey1000" weight="semibold">
                {bundlePrice}
              </Text.Normal>
              <Text.Normal size="200" color="grey700" weight="semibold" stroked>
                {originalAmount}
              </Text.Normal>
              <Tag inline leftItem="circle" type="correct" size="small">
                -{bundle.pricing.reducedPercentage}%
              </Tag>
            </>
          ) : (
            <Text.Normal size="200" color="grey1000" weight="semibold">
              {originalAmount}
            </Text.Normal>
          )}
        </Box>
        {isBooked ? (
          <PremiumOfferBookedBadge />
        ) : (
          /* TODO: add modal on button click */
          <F0Button
            variant="outline"
            label={i18n.t('ats.job_details.marketplace.bundles.buy_button')}
            onClick={() => setPurchaseModalOpenId(bundle.id)}
            disabled={isBooked}
          />
        )}

        {purchaseModalOpenId === bundle.id && (
          <BundleCheckoutModal
            bundle={bundle}
            isOpen={purchaseModalOpenId === bundle.id}
            onClose={() => setPurchaseModalOpenId(null)}
          />
        )}
      </Box>
    </Box>
  )
}
